import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {http} from 'hiya-common/src/utils/axios'
import {useSelector} from 'react-redux'

export interface WeekTaskSt {
  week_pearls: number
  max_target: number
  total_reward: number
  receive_reward: number
  task_status: number
  task_id?: string
  task_level?: string
  task_param?: number
}

export interface LevelBandItem {
  level: string
  min_score: number
  max_score: number
}

export interface LevelV2DailyTaskTier {
  target: number
  reward_score: number
  done: boolean
}

export interface LevelV2DailyTaskItem {
  task_id: number
  icon: string
  current_value: number
  tiers: LevelV2DailyTaskTier[]
}

export interface PerformanceItem {
  metric_id: number
  target: number
  current: number
  score_granted: boolean
  jump_url: string
  reward_score: number
  target_s: number
  target_ss: number
}

export interface LevelCenterPopup {
  show_upgrade: boolean
  show_demotion: boolean
  show_retention: boolean
  show_fast_track: boolean
}

/** `/live/hiya_socialize/fetch_level_benefit_usage` */
export interface LevelBenefitUsageFields {
  week_start: string
  match_dispatch_cnt: number
  feed_exposure_pv: number
  deep_chat_dispatch_cnt: number
}

export interface RankCenterActiveState extends LevelBenefitUsageFields {
  avatar: string
  user_level: string
  need_auth: boolean
  week_remain_ts: number
  level_v2_daily_pearl_total: number
  week_task: WeekTaskSt
  level_v2_income_score: number
  level_v2_task_score: number
  level_v2_perf_score: number
  level_v2_composite_score: number
  level_bands: LevelBandItem[]
  level_effective_start: string
  level_effective_end: string
  fast_track_active: boolean
  level_v2_daily_tasks: LevelV2DailyTaskItem[]
  performance_items: PerformanceItem[]
  level_center_popup: LevelCenterPopup
  benefits_url: string
}

interface RankCenterState {
  active: RankCenterActiveState
}

const initialState: RankCenterState = {
  active: {
    week_start: '',
    match_dispatch_cnt: 0,
    feed_exposure_pv: 0,
    deep_chat_dispatch_cnt: 0,
    avatar: '',
    user_level: 'N',
    need_auth: false,
    week_remain_ts: 0,
    level_v2_daily_pearl_total: 0,
    week_task: {
      week_pearls: 0,
      max_target: 0,
      total_reward: 0,
      receive_reward: 0,
      task_status: 0,
    },
    level_v2_income_score: 0,
    level_v2_task_score: 0,
    level_v2_perf_score: 0,
    level_v2_composite_score: 0,
    level_bands: [],
    level_effective_start: '',
    level_effective_end: '',
    fast_track_active: false,
    level_v2_daily_tasks: [],
    performance_items: [],
    level_center_popup: {
      show_upgrade: false,
      show_demotion: false,
      show_retention: false,
      show_fast_track: false,
    },
    benefits_url: '',
  },
}

export const fetch_female_level_center_page = createAsyncThunk(
  'RankCenter/fetch_female_level_center_page',
  async (params: Record<string, never>, store: any) => {
    const resp = await http.post(
      '/live/hiya_socialize/fetch_female_level_center_page',
      {
        ...params,
      },
      {store, xcErrorToast: true}
    )
    return (resp?.data?.data || {}) as Partial<RankCenterActiveState>
  }
)

export const fetch_level_benefit_usage = createAsyncThunk(
  'RankCenter/fetch_level_benefit_usage',
  async (params: Record<string, never>, store: any) => {
    const resp = await http.post(
      '/live/hiya_socialize/fetch_level_benefit_usage',
      {...params},
      {store, xcErrorToast: true}
    )
    return (resp?.data?.data || {}) as Partial<RankCenterActiveState>
  }
)

export type AcquireFemaleTaskRewardParams = {
  task_id?: string
  task_type: 'novice_task' | 'daily_task' | 'weekly_task'
  task_level?: string
  task_param?: number
}

export const acquire_female_task_reward = createAsyncThunk(
  'RankCenter/acquire_female_task_reward',
  async (params: AcquireFemaleTaskRewardParams, store: any) => {
    const resp = await http.post(
      '/live/hiya_socialize/acquire_female_task_reward',
      {
        ...params,
      },
      {store, xcErrorToast: true}
    )
    return resp?.data?.data || {}
  }
)

export const RankCenterSlice = createSlice({
  name: 'RankCenter',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      fetch_female_level_center_page.fulfilled,
      (state, action) => {
        Object.assign(state.active, action.payload || {})
      }
    )
    builder.addCase(fetch_level_benefit_usage.fulfilled, (state, action) => {
      Object.assign(state.active, action.payload || {})
    })
  },
})

export default RankCenterSlice.reducer
export const RankCenterActions = RankCenterSlice.actions
export const useRankCenterState = () =>
  useSelector((store: any) => store.RankCenter) as RankCenterState
